import { logout, silentRefresh, verifyAuthToken } from 'utils/auth';
import type { TSilentRefreshResult } from 'utils/auth';
import { endSession, isSessionActive, userSession } from 'state/session';
import { getStorageItem } from 'utils/ui/storage';
import logger from 'utils/log';

/** Refresh this many ms before the access token is due to expire. */
export const REFRESH_LEAD_MS = 60_000;
/** Cadence for re-checking while no valid token is present (pre-login, customer sessions) or after a failed refresh. */
export const RECHECK_INTERVAL_MS = 30_000;
/** Floor for the refresh delay so an already-expired token cannot schedule in the past. */
export const MIN_REFRESH_DELAY_MS = 1_000;

/**
 * Reads the stored auth token, treating any storage failure as "no token" rather than
 * letting it crash the refresh loop (getStorageItem throws on storage/parse errors).
 */
function readAuthToken(): unknown {
  try {
    return getStorageItem('sessionStorage', 'auth:token');
  } catch (error) {
    logger.warn('[tokenRefreshScheduler] Failed to read auth token', { error });
    return null;
  }
}

/**
 * Ends the dealer's login after the token endpoint rejected `refreshToken` for good. Acts only while
 * the stored token still carries that refresh token, so a slow rejection never ends a newer login.
 * Ends an active session first, as the logout menu does: once logout() drops `auth:token`, the
 * unload handler can no longer end it. Never throws.
 */
async function endRejectedLogin(refreshToken: string): Promise<void> {
  try {
    const token = readAuthToken();
    if (!verifyAuthToken(token) || token.refreshToken !== refreshToken) return;
    if (isSessionActive(userSession().get())) {
      try {
        await endSession({ isLogout: true, keepAlive: true });
      } catch (error) {
        logger.error('[tokenRefreshScheduler] Failed to end the session before logout', { error });
      }
    }
    await logout();
  } catch (error) {
    logger.error('[tokenRefreshScheduler] Failed to end the rejected login', { error });
  }
}

// --- Single-owner-per-page scheduler ---------------------------------------
// The scheduler is a module-level singleton guarded by a reference count: the loop starts
// when the first owner acquires it (count 0 -> 1) and is fully torn down when the last
// releases it (count -> 0). Two loops on one page would fire concurrent silentRefresh()
// calls with the same refresh token near expiry.

let refCount = 0;
let timer: ReturnType<typeof setTimeout> | null = null;
// Bumped on every start AND stop. Each scheduler run captures the generation it
// belongs to; a queued timer or an in-flight silentRefresh() that resolves after a
// stop/restart sees a stale generation and bails instead of re-arming. A single
// `cancelled` boolean is not enough: a stop-while-refresh-in-flight followed by a
// restart would reset it to false and let the stale callback arm a SECOND timer
// chain alongside the new one — the very double-refresh this guard prevents.
let generation = 0;
// Refresh token the IdP rejected for good; never sent again, even after a restart.
let rejectedRefreshToken: string | null = null;

const rearm = (gen: number, delay: number): void => {
  if (gen !== generation) return;
  timer = setTimeout(() => run(gen), delay);
};

const handleResult = async (gen: number, result: TSilentRefreshResult): Promise<void> => {
  if (result.status === 'rejected') rejectedRefreshToken = result.refreshToken;
  if (gen !== generation) return;
  if (result.status === 'rejected') await endRejectedLogin(result.refreshToken);
  // Re-arm against the new expiry on success; otherwise back off to the re-check
  // cadence so we never hot-loop the token endpoint.
  rearm(gen, result.status === 'refreshed' ? 0 : RECHECK_INTERVAL_MS);
};

const run = (gen: number): void => {
  if (gen !== generation) return;
  const token = readAuthToken();

  if (!verifyAuthToken(token) || token.refreshToken === rejectedRefreshToken) {
    // Nothing to refresh yet, or only a credential that can never refresh — re-check later
    // (cheap no-op for customer sessions; picks up a new login).
    rearm(gen, RECHECK_INTERVAL_MS);
    return;
  }

  const delay = Math.max(token.expiresAt * 1000 - Date.now() - REFRESH_LEAD_MS, MIN_REFRESH_DELAY_MS);
  timer = setTimeout(() => {
    // A rejection from a previous cycle may have landed while this one waited.
    if (token.refreshToken === rejectedRefreshToken) return run(gen);
    void silentRefresh()
      .then((result) => handleResult(gen, result))
      .catch((error: unknown) => {
        logger.warn('[tokenRefreshScheduler] Refresh cycle failed', { error });
        rearm(gen, RECHECK_INTERVAL_MS);
      });
  }, delay);
};

/**
 * Starts the silent token refresh loop on the first call; later calls only count owners.
 * The loop refreshes shortly before the access token expires, backs off to the re-check cadence
 * on a transient failure (network error, 5xx), and ends the login on a 400/401, which is final for
 * that refresh token. It is a no-op while no token is stored (e.g. the customer exhibition).
 */
export function acquireTokenRefresh(): void {
  refCount += 1;
  if (refCount !== 1) return;
  generation += 1;
  run(generation);
}

/** Releases one owner; the last release stops the loop and cancels any queued or in-flight cycle. */
export function releaseTokenRefresh(): void {
  refCount -= 1;
  if (refCount !== 0) return;
  generation += 1;
  if (timer) {
    clearTimeout(timer);
    timer = null;
  }
}
